import { useState } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import MedicationGroupByRoute, { createEmptyMedication } from './MedicationGroupByRoute';

export default function NewPrescriptionModal({ open, onClose, patient, onSuccess }) {
  const [prescriptionName, setPrescriptionName] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Novo estado: grupos de medicamentos por via
  const [medicationGroups, setMedicationGroups] = useState([
    {
      via: 'ORAL',
      medicamentos: [createEmptyMedication()]
    }
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que há pelo menos um medicamento válido em algum grupo
    const hasValidMedication = medicationGroups.some(group => 
      group.medicamentos.some(med => med.name.trim() !== '')
    );
    
    if (!hasValidMedication) {
      toast.error('Adicione pelo menos um medicamento');
      return;
    }

    setLoading(true);
    try {
      // Criar UMA ÚNICA receita com todos os grupos de vias
      const groupsWithMeds = medicationGroups
        .map(group => ({
          via: `USO ${group.via}`,
          medicamentos: group.medicamentos.filter(med => med.name.trim() !== '')
        }))
        .filter(group => group.medicamentos.length > 0);
      
      // Flatten all medications with their via info
      const allMedicamentos = [];
      for (const group of groupsWithMeds) {
        for (const med of group.medicamentos) {
          allMedicamentos.push({
            ...med,
            via: group.via  // Add via to each medication
          });
        }
      }
      
      // Determine uso: if multiple vias, list them; otherwise single via
      const uniqueVias = [...new Set(groupsWithMeds.map(g => g.via))];
      const uso = uniqueVias.length === 1 ? uniqueVias[0] : uniqueVias.join(' / ');
      
      await axiosInstance.post('/prescriptions', {
        patient_id: patient.id,
        medicamentos: allMedicamentos,
        uso: uso,
        name: prescriptionName,
        medication_groups: groupsWithMeds  // Send groups for structured printing
      });

      toast.success('Receita criada com sucesso!');
      onSuccess();
      handleReset();
      onClose();
    } catch (error) {
      console.error('Erro ao criar receita:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao criar receita';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPrescriptionName('');
    setMedicationGroups([
      {
        via: 'ORAL',
        medicamentos: [createEmptyMedication()]
      }
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Nova Receita - {patient?.nome}</DialogTitle>
          <DialogDescription>
            Adicione uma nova receita para este paciente. 
            Você pode incluir medicamentos de diferentes vias de administração.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nome da Receita */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label className="text-sm font-medium mb-2 block">
              Nome da Receita (opcional)
            </Label>
            <Input
              value={prescriptionName}
              onChange={(e) => setPrescriptionName(e.target.value)}
              placeholder="Ex: Medicação pós-operatória, Tratamento crônico..."
              className="h-12 bg-white"
              data-testid="new-rx-prescription-name"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Dê um nome para identificar esta receita mais facilmente
            </p>
          </div>

          {/* Medicamentos agrupados por via */}
          <MedicationGroupByRoute
            groups={medicationGroups}
            onChange={setMedicationGroups}
            dataTestIdPrefix="new-rx-"
          />

          {/* Botões de ação */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="rounded-full px-8"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8"
              data-testid="new-rx-submit-button"
            >
              {loading ? 'Salvando...' : 'Salvar Receita'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
